import React from 'react';
import ReactDOMServer from 'react-dom/server';
import cloneDeep from 'lodash/cloneDeep';
import cn from 'classnames';
import { Periods } from '@/creditflowresearch/ui/common/constants';
import { format, getQuarter, getTime, getYear } from 'date-fns';
import { MONTH_YEAR_FORMAT } from '@dealroadshow/uikit-date-time/dateFormats';
import getUTCDate from '@framework/date-helpers/getUTCDate';
import colors from './colors';
import ChartTooltip from './ChartTooltip';

import styles from './chartTooltip.module.scss';

export const getFullChartConfig = (
  collection,
  options,
) => {
  const {
    showVolume,
    showStackedData,
    period,
  } = options;

  return {
    chart: {
      animation: false,
      type: 'column',
      backgroundColor: 'transparent',
      spacing: [0, 4, 0, 0],
      panning: {
        enabled: true,
        type: 'x',
      },
      zooming: {
        mouseWheel: {
          enabled: false,
        },
      },
      events: {},
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 300,
        },
        chartOptions: {
          chart: {
            spacingRight: 0,
          },
          legend: {
            padding: 20,
            margin: -30,
            align: 'center',
          },
          navigator: {
            enabled: false,
          },
          yAxis: {
            labels: {
              enabled: false,
            },
          },
        },
      }],
    },
    time: {
      useUTC: true,
    },
    rangeSelector: false,
    credits: {
      enabled: false,
    },
    legend: {
      borderWidth: 0,
      enabled: true,
      padding: 20,
      width: '100%',
      shadow: false,
      symbolRadius: 0,
      symbolWidth: 10,
      symbolHeight: 10,
      itemStyle: {
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '11px',
        lineHeight: '13px',
        color: 'var(--text-neutral-body)',
      },
      itemMarginBottom: 0,
      itemMarginTop: 5,
      itemDistance: 20,
      alignColumns: false,
      navigation: {
        enabled: false,
      },
    },
    scrollbar: {
      enabled: false,
    },
    title: {
      text: '',
    },
    xAxis: getDataFormatterXaxis(period),
    yAxis: getDataFormatterYaxis(showVolume),
    plotOptions: getPlotOptions(period, showStackedData),
    tooltip: getTooltipConfig(showStackedData, period, showVolume),
    navigator: {
      outlineWidth: 0,
      annotationsOptions: {
        visible: true,
      },
      startOnTick: true,
      alignTicks: true,
      endOnTick: false,
      height: 25,
      maskFill: 'rgba(74, 119, 181, 0.2)',
      handles: {
        backgroundColor: '#4a76b6',
        height: 11,
        width: 9,
        lineWidth: 0,
        symbols: ['customHandler', 'customHandler'],
      },
      xAxis: getDataFormatterNavigatorXaxis(period),
      series: {
        showInLegend: false,
        type: 'column',
        color: '#ccc',
        ...getChartDataShowingType(showStackedData),
      },
    },
    series: {},
  };
};

export const getPlotOptions = (period: Periods, showStackedData: boolean) => ({
  bar: {
    dataLabels: {
      enabled: true,
    },
  },
  column: {
    pointPlacement: null,
    minPointLength: 5,
    borderRadius: 0,
  },
  series: {
    showInLegend: true,
    showInNavigator: true,
    type: 'column',
    color: '#ccc',
    animation: false,
    connectNulls: true,
    stickyTracking: false,
    borderWidth: 0,
    marker: {
      enabled: false,
      radius: 3,
    },
    ...getDataGroupingConfig(period),
    ...getChartDataShowingType(showStackedData),
  },
});

export const getDataGroupingConfig = (period: Periods) => {
  if (period === Periods.YEAR) {
    return {
      dataGrouping: undefined,
    };
  }
  if (period === Periods.QUARTER) {
    return {
      dataGrouping: {
        enabled: true,
        forced: true,
        units: [[
          'month',
          [3],
        ]],
      },
    };
  }
  if (period === Periods.MONTH) {
    return {
      dataGrouping: {
        enabled: true,
        forced: true,
        units: [[
          'month',
          [1],
        ]],
      },
    };
  }
  return undefined;
};

export function getDataFormatterNavigatorXaxis(period: Periods) {
  if (period === Periods.YEAR) {
    return {
      lineColor: '#c8c8c8',
      gridLineWidth: 1, // default
      tickPixelInterval: 200, // default
      labels: {
        align: 'center',
        y: 25,
        style: {
          color: '#c8c8c8',
          textOutline: 0,
          fontSize: '11px',
        },
        formatter() {
          return this.value;
        },
      },
    };
  }
  if (period === Periods.QUARTER) {
    return {
      lineColor: '#c8c8c8',
      gridLineWidth: 0,
      tickPixelInterval: 200,
      labels: {
        y: 25,
        x: -50,
        style: {
          color: '#c8c8c8',
          textOutline: 0,
          fontSize: '11px',
        },
        formatter() {
          const quarter = getQuarter(getUTCDate(new Date(this.value)));
          const year = getYear(getUTCDate(new Date(this.value)));
          return `${ quarter }Q ${ year }`;
        },
      },
    };
  }
  if (period === Periods.MONTH) {
    return {
      type: 'datetime',
      lineColor: '#c8c8c8',
      gridLineWidth: 1,
      minTickInterval: 160,
      labels: {
        y: 25,
        x: -70,
        style: {
          color: '#c8c8c8',
          textOutline: 0,
          fontSize: '11px',
        },
        formatter() {
          return format(getUTCDate(new Date(this.value)), MONTH_YEAR_FORMAT);
        },
      },
    };
  }
  return undefined;
}

export function getDataFormatterXaxis(period: Periods) {
  if (period === Periods.YEAR) {
    return {
      type: 'datetime',
      lineColor: '#c8c8c8',
      labels: {
        startOnTick: false,
        min: null,
        style: {
          color: 'var(--text-neutral-body)',
          textOutline: 0,
          fontSize: '11px',
        },
        formatter() {
          return this.value;
        },
      },
    };
  }
  if (period === Periods.QUARTER) {
    return {
      labels: {
        startOnTick: true,
        min: 10000,
        style: {
          color: 'var(--text-neutral-body)',
          textOutline: 0,
          fontSize: '11px',
        },
        formatter() {
          const quarter = getQuarter(getUTCDate(new Date(this.value)));
          const year = getYear(getUTCDate(new Date(this.value)));

          return `${ quarter }Q ${ year }`;
        },
      },
    };
  }
  if (period === Periods.MONTH) {
    return {
      type: 'datetime',
      lineColor: '#c8c8c8',
      labels: {
        startOnTick: false,
        min: null,
        style: {
          color: 'var(--text-neutral-body)',
          textOutline: 0,
          fontSize: '11px',
        },
        formatter() {
          return format(getUTCDate(new Date(this.value)), MONTH_YEAR_FORMAT);
        },
      },
    };
  }

  return undefined;
}

export function getDataFormatterYaxis(showVolume: boolean) {
  return {
    endOnTick: true,
    gridLineWidth: 1,
    gridLineColor: '#f3f3f3',
    lineColor: '#c0c0c0',
    lineWidth: 1,
    tickAmount: 10,
    labels: {
      enabled: true,
      style: {
        color: 'var(--text-neutral-body)',
        fontSize: '9px',
        width: '175px',
      },
      formatter() {
        let labelNumber = showVolume ? this.value / 1000 : this.value;

        if (!showVolume) {
          return labelNumber;
        }

        return `$${ labelNumber }b`;
      },
    },
    opposite: false,
    title: {
      text: '',
    },
  };
}

export const getConfigureChartByFilters = (data, period: Periods) => {
  if (period === Periods.YEAR) {
    return getGroupedDealByYear(data);
  }
  if (period === Periods.QUARTER) {
    return getGroupedDealByQuarter(data);
  }
  if (period === Periods.MONTH) {
    return getGroupedDealByMonth(data);
  }

  return undefined;
};

const getChartDataShowingType = (showStackedData: boolean) => {
  if (showStackedData) {
    return {
      stacking: 'normal',
    };
  }

  return undefined;
};

export function getTooltipConfig(showStackedData: boolean, period: Periods, showVolume: boolean) {
  return {
    outside: true,
    split: false,
    enabled: true,
    shared: true,
    useHTML: true,
    followTouchMove: false,
    valueDecimals: 2,
    padding: 0,
    backgroundColor: '#fff',
    borderWidth: 0,
    className: showVolume ? styles.container : cn(styles.container, styles.withoutDealSize),
    formatter() {
      return ReactDOMServer.renderToString(
        <ChartTooltip
          showStackedData={ showStackedData }
          period={ period }
          showVolume={ showVolume }
          points={ this.points }
        />,
      );
    },
  };
}

const getGroupedDealByQuarter = (data) => {
  const chartQuarterData = {
    byCount: [],
    byVolume: [],
    lastDate: 0,
  };
  const chartData = cloneDeep(data);

  chartData.forEach((item, elemIndex) => {
    let outPutVolume = [];
    let outPutCount = [];
    let yearsVolume = {};
    let yearsCount = {};

    for (let i = 0; i < item.dealChartData.length; i++) {
      const current = item.dealChartData[i];

      const dateObj = new Date(current.date);
      const utcDate = getUTCDate(dateObj);
      const currentDate = getTime(utcDate);
      const currentQuarter = getQuarter(utcDate);
      const currentYear = getYear(utcDate);

      if (chartQuarterData.lastDate < currentDate) {
        chartQuarterData.lastDate = currentDate;
      }

      if (!(currentYear in yearsVolume)) {
        yearsVolume[currentYear] = {};
        yearsCount[currentYear] = {};
      }

      if (!(currentQuarter in yearsVolume[currentYear])) {
        yearsVolume[currentYear][currentQuarter] = {
          x: currentDate,
          y: 0,
          dealSize: 0,
          dealCount: 0,
        };
        yearsCount[currentYear][currentQuarter] = {
          x: currentDate,
          y: 0,
          dealSize: 0,
          dealCount: 0,
        };
      }

      yearsCount[currentYear][currentQuarter].y += current.dealCount;
      yearsCount[currentYear][currentQuarter].dealSize += current.dealSize;
      yearsCount[currentYear][currentQuarter].dealCount += current.dealCount;

      yearsVolume[currentYear][currentQuarter].y += current.dealSize;
      yearsVolume[currentYear][currentQuarter].dealSize += current.dealSize;
      yearsVolume[currentYear][currentQuarter].dealCount += current.dealCount;
    }

    Object.keys(yearsVolume).forEach((year) => {
      outPutVolume.push(...Object.values(yearsVolume[year]));
      outPutCount.push(...Object.values(yearsCount[year]));
    });

    chartQuarterData.byVolume.push({
      name: item.name,
      color: item.color || colors[elemIndex],
      data: outPutVolume,
    });
    chartQuarterData.byCount.push({
      name: item.name,
      color: item.color || colors[elemIndex],
      data: outPutCount,
    });
  });

  return chartQuarterData;
};

const getGroupedDealByMonth = (data) => {
  let chartMonthData = {
    byCount: [],
    byVolume: [],
    lastDate: 0,
  };
  const chartData = cloneDeep(data);

  chartData.forEach((item, elemIndex) => {
    const months = {
      byCount: [],
      byVolume: [],
    };

    item.dealChartData.forEach((item) => {
      const date = getTime(new Date(item.date));

      // Save the last deal unix time
      if (chartMonthData.lastDate < date) {
        chartMonthData.lastDate = date;
      }

      months.byVolume.push({
        x: date,
        y: item.dealSize,
        dealSize: item.dealSize,
        dealCount: item.dealCount,
      });

      months.byCount.push({
        x: date,
        y: item.dealCount,
        dealSize: item.dealSize,
        dealCount: item.dealCount,
      });
    });

    chartMonthData.byVolume.push({
      name: item.name,
      color: item.color || colors[elemIndex],
      data: months.byVolume,
    });
    chartMonthData.byCount.push({
      name: item.name,
      color: item.color || colors[elemIndex],
      data: months.byCount,
    });
  });

  return chartMonthData;
};

const getGroupedDealByYear = (data) => {
  const chartYearData = {
    byCount: [],
    byVolume: [],
    lastDate: 0,
  };
  const chartData = cloneDeep(data);

  chartData.forEach((item, elemIndex) => {
    let yearsVolume = {};
    let yearsCount = {};

    for (let i = 0; i < item.dealChartData.length; i++) {
      const currentElement = item.dealChartData[i];

      const dateObj = new Date(currentElement.date);
      const utcDate = getUTCDate(dateObj);
      const currentYear = getYear(utcDate);

      if (chartYearData.lastDate < currentYear) {
        chartYearData.lastDate = currentYear;
      }

      if (!(currentYear in yearsVolume)) {
        yearsVolume[currentYear] = {
          x: currentYear,
          y: 0,
          dealSize: 0,
          dealCount: 0,
        };
        yearsCount[currentYear] = {
          x: currentYear,
          y: 0,
          dealSize: 0,
          dealCount: 0,
        };
      }

      yearsVolume[currentYear].dealSize += currentElement.dealSize;
      yearsVolume[currentYear].dealCount += currentElement.dealCount;
      yearsVolume[currentYear].y += currentElement.dealSize;

      yearsCount[currentYear].dealSize += currentElement.dealSize;
      yearsCount[currentYear].dealCount += currentElement.dealCount;
      yearsCount[currentYear].y += currentElement.dealCount;
    }

    chartYearData.byVolume.push({
      name: item.name,
      color: item.color || colors[elemIndex],
      data: Object.values(yearsVolume),
    });
    chartYearData.byCount.push({
      name: item.name,
      color: item.color || colors[elemIndex],
      data: Object.values(yearsCount),
    });
  });

  return chartYearData;
};
